<template>
  <div
    v-bind='$attrs'
    class='flex flex-row items-center gap-1'
  >
    <label
      v-if='label'
      :for='name'
      class='block text-sm font-normal'
      :class='[
        (disabled && editMode) ? "text-gray-400" : "text-gray-700",
        editMode ? "w-full max-w-[50%]" : "",
      ]'
      data-cy='label'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <div
      v-if='editMode'
      class='w-full'
    >
      <div class='relative mt-1 w-full'>
        <DatePicker
          v-slot='{ inputValue, inputEvents, togglePopover, hidePopover }'
          v-model='proxyValue'
          :masks='masks'
          mode='date'
          is24hr
          :timezone='timezone'
          :attributes='[
            {
              key: "today",
              dates: new Date(),
              highlight: true,
              customData: { isToday: true },
            }
          ]'
          :disabled='disabled'
          :popover='{ visibility: disabled ? "hidden" : "click" }'
        >
          <input
            :id='name'
            :value='inputValue'
            :name='name'
            type='text'
            :placeholder='placeholder'
            :aria-describedby='ariaDescribedBy'
            :disabled='disabled'
            data-cy='input'
            class='block w-full rounded-md text-sm shadow-sm'
            :class='[
              error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
              disabled ? "bg-gray-50 text-gray-400" : "",
              inputClasses
            ]'
            v-on='inputEvents'
            @focusin='() => { togglePopover() }'
            @focusout='hidePopover'
          >
          <div class='pointer-events-none absolute inset-y-0 right-3 flex items-center py-3 text-gray-400'>
            <CalendarIcon
              class='h-6 w-6 shrink-0'
              aria-hidden='true'
            />
          </div>
        </DatePicker>
      </div>
      <InputError :error='error'/>
    </div>
    <div v-else>
      <DatePicker
        v-slot='{ inputValue }'
        v-model='proxyValue'
        :masks='masks'
        mode='date'
        is24hr
        :timezone='timezone'
        :disabled='disabled'
        :popover='{ visibility: false }'
      >
        <p
          class='text-sm'
          data-cy='preview-value'
        >
          {{ inputValue }}
        </p>
      </DatePicker>
    </div>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>

</template>

<script lang="ts">
  import { defineComponent, ref, watch, onMounted } from 'vue'
  import useInputFieldBase, { InputFieldProps,
                              InputFieldComponents,
                              InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { DatePicker } from 'v-calendar'
  import { CalendarIcon } from '@heroicons/vue/outline'
  import 'v-calendar/dist/style.css'
  import { useToString } from '@vueuse/core'
  import { useDateFormat } from '@vueuse/shared/index'

  export default defineComponent({
    name: 'DateField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      DatePicker,
      CalendarIcon,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      timezone: {
        type: String,
        default: 'UTC',
      },
    },
    emits: [
      ...InputFieldEmits,
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, getLabel } = useInputFieldBase()

      const masks = {
        input: ['DD.MM.YYYY'],
      }
      const proxyValue = ref()

      watch(proxyValue, (value) => {
        if (typeof value === 'object') {
          if (value === null) updateValue('')
          else formatDate(value)
        }
      })
      const formatDate = (date: Date) => {
        try {
          const year = date.getFullYear()
          const month = date.getMonth() + 1 <= 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1
          const day = date.getDate() <= 9 ? '0' + date.getDate() : date.getDate()

          const formattedDate = year + '-' + month + '-' + day

          updateValue(formattedDate)
        } catch (error) {

        }

      }

      watch(() => props.modelValue, (value) => {
        if (value) {
          if(useToString(value)) {
            proxyValue.value = Date.parse(value)
          } else {
            proxyValue.value = value
          }
        }else{
          proxyValue.value = null
        }
      })

      onMounted(() => {
        if(props.modelValue) {
          if(useToString(props.modelValue)) {
            proxyValue.value = Date.parse(props.modelValue)
          } else {
            proxyValue.value = props.modelValue
          }
        }
      })

      return {
        updateValue, helpVisible, setHelpModalVisibility, masks, proxyValue, getLabel, useDateFormat,
      }
    },
  })
</script>
